import * as React from 'react'
import styled from 'styled-components'
import { DateRangePicker } from 'react-dates'
import { DATE_FORMAT } from 'static/constants'
import SmallDateRangePickerCustomStyle from 'static/smallDateRangePickerCustomStyle'
import moment from 'moment'
import I18n from 'core/i18n'
import * as constants from '../../static/constants'
import { Button, Select } from 'components/atoms'

type HandleOnChangeDateRanges = (
  index: number,
  startTime?: moment.Moment,
  endTime?: moment.Moment
) => void

interface IRangeTagInput {
  id?: number
  value: string
  start: moment.Moment | undefined
  end: moment.Moment | undefined
}
interface IProps {
  rangeTags: Array<{ value: string; label: string }>
  selectedRangeTags: IRangeTagInput[]
  setSelectedRangeTags: React.Dispatch<React.SetStateAction<IRangeTagInput[]>>
}

type IDateRangeProps = IRangeTagInput & {
  index: number
  rangeTags: Array<{ value: string; label: string }>
  handleOnChangeTag: (index: number, e: any) => void
  handleOnChangeDateRange: HandleOnChangeDateRanges
  handleOnDelete: (index: number) => void
}

const MinDateRange = ({
  value,
  start,
  end,
  index,
  rangeTags,
  handleOnChangeTag,
  handleOnChangeDateRange,
  handleOnDelete,
}: IDateRangeProps) => {
  const [focusedInput, setFocusedInput] = React.useState(null)

  const onDateRangePickerChange = (dates: {
    startDate: moment.Moment | null
    endDate: moment.Moment | null
  }) => {
    handleOnChangeDateRange(index, dates.startDate, dates.endDate)
  }
  const onTagChange = e => {
    handleOnChangeTag(e.target.value, index)
  }
  const changeFocusedInput = (changedFocusedInput: any) => {
    setFocusedInput(changedFocusedInput)
  }

  return (
    <Wrapper>
      <p className="DateInput_Title">
        <Select
          options={[{ value: '', label: I18n.t('generic.select') }, ...rangeTags]}
          name={'range_tag'}
          defaultValue={value}
          onChangeHandler={onTagChange}
        />
      </p>

      <div className="DateInput_Main">
        <SmallDateRangePickerCustomStyle />
        <DateRangePicker
          startDate={start}
          startDateId="start_date"
          endDate={end}
          endDateId="end_date"
          onDatesChange={onDateRangePickerChange}
          focusedInput={focusedInput}
          onFocusChange={(currentFocusedInput: string | null) =>
            changeFocusedInput(currentFocusedInput)
          }
          numberOfMonths={1}
          isOutsideRange={() => false}
          hideKeyboardShortcutsPanel={true}
          displayFormat={DATE_FORMAT}
          minimumNights={0}
          openDirection="up"
        />
        <Button
          small={true}
          textColor={constants.COLORS.white}
          backgroundColor={constants.COLORS.caution}
          handleClick={() => {
            handleOnDelete(index)
          }}
        >
          {I18n.t('generic.delete')}
        </Button>
      </div>
    </Wrapper>
  )
}

const MultipleDateRangeInput = ({ rangeTags, selectedRangeTags, setSelectedRangeTags }: IProps) => {
  const onChangeRangeTagsSelect = React.useCallback((value, targetIndex) => {
    setSelectedRangeTags(oldValues => {
      return oldValues.map((tag, i) => {
        return i === targetIndex ? { ...tag, value: String(value) } : tag
      })
    })
  }, [])

  const handleOnChangeDateRange = React.useCallback((index, start, end) => {
    setSelectedRangeTags(oldValues => {
      return oldValues.map((o, i) => (i === index ? { ...o, start, end } : o))
    })
  }, [])
  const handleOnDelete = React.useCallback((index: number) => {
    setSelectedRangeTags(oldValues => oldValues.filter((_, i) => i !== index))
  }, [])

  const handleAddRangeTag = React.useCallback(() => {
    setSelectedRangeTags(oldValues => [
      ...oldValues,
      { value: '', label: '', start: undefined, end: undefined },
    ])
  }, [])

  return (
    <DateRanges>
      {selectedRangeTags.map((selectedRangeTag, i) => (
        <MinDateRange
          index={i}
          {...selectedRangeTag}
          rangeTags={rangeTags}
          handleOnChangeTag={onChangeRangeTagsSelect}
          handleOnChangeDateRange={handleOnChangeDateRange}
          handleOnDelete={handleOnDelete}
          key={`${selectedRangeTag.value}-${i}`}
        />
      ))}
      <Button small={true} handleClick={handleAddRangeTag}>
        + {I18n.t('generic.add')}
      </Button>
    </DateRanges>
  )
}

export default MultipleDateRangeInput

const DateRanges = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const Wrapper = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;

  @media (max-width: ${constants.BREAKPOINT_TABLET_SMALL}px) {
    padding: 0 8px;
    flex-direction: column;
  }
  .DateInput_Title {
    max-width: 280px;
    font-size: 14px;
    font-weight: bold;
    line-height: 1.2;
    margin-right: 8px;
    @media (max-width: ${constants.BREAKPOINT_TABLET_SMALL}px) {
      margin-top: 8px;
      margin-right: 0;
      width: 100%;
      max-width: 100%;
    }
  }
  .DateInput_Main {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    gap: 12px;
    @media (max-width: ${constants.BREAKPOINT_TABLET_SMALL}px) {
      width: 100%;
    }
  }
`
